import { useEffect, useState, useCallback } from "react";
import { FileText, Trash2, Printer, Eye, Sparkles, GitCompareArrows, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { centralApi } from "@/lib/api";
import { periodLabel } from "@/lib/wellness";
import { fmtMoney } from "@/lib/finance";
import { statusStyles, scoreTone, diffSnapshots } from "@/lib/central";
import { PageHeader, SectionCard, EmptyState } from "@/components/shared";
import { SelectField } from "@/components/forms";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

const fmtWhen = (iso) => { try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return ""; } };

export default function CentralReports() {
  const [reports, setReports] = useState(null);
  const [active, setActive] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [aId, setAId] = useState("");
  const [bId, setBId] = useState("");

  const load = useCallback(async () => {
    try { const d = await centralApi.reports(); setReports(d.reports || []); }
    catch { toast.error("Could not load reports"); setReports([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try { await centralApi.deleteReport(id); setReports((r) => r.filter((x) => x.id !== id)); toast.success("Report deleted"); }
    catch { toast.error("Could not delete"); }
  };

  if (reports === null) return <Skeleton className="h-96 rounded-[14px]" />;

  const opts = reports.map((r) => ({ value: r.id, label: `${r.title} (${r.horizon})` }));
  const repA = reports.find((r) => r.id === aId);
  const repB = reports.find((r) => r.id === bId);
  // "Later" report is compared against the "earlier" one (by created_at).
  let newer = repA, older = repB;
  if (repA && repB && new Date(repA.created_at) < new Date(repB.created_at)) { newer = repB; older = repA; }
  const deltas = newer && older ? diffSnapshots(newer.snapshot, older.snapshot) : [];

  return (
    <div data-testid="central-reports">
      <PageHeader title="Reports" subtitle="Saved snapshots of how you're doing overall — revisit, compare and print." testid="central-reports-header"
        actions={reports.length >= 2 && <Button variant={compareMode ? "default" : "outline"} size="sm" onClick={() => setCompareMode((v) => !v)} data-testid="compare-toggle-button" className={compareMode ? "bg-[var(--green)] text-[#f7fbf9]" : "border-[color:var(--border-subtle)]"}><GitCompareArrows className="h-4 w-4 mr-1.5" />Compare</Button>} />

      {compareMode && reports.length >= 2 && (
        <SectionCard className="mb-4" title="Compare two reports" subtitle="See exactly what improved between them" testid="compare-panel">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <SelectField label="Report A" value={aId} onChange={setAId} options={opts} testid="compare-select-a" />
            <SelectField label="Report B" value={bId} onChange={setBId} options={opts} testid="compare-select-b" />
          </div>
          {newer && older && newer.id !== older.id ? (
            <div className="mt-4">
              <p className="text-[12px] text-[var(--text-muted)] mb-2" data-testid="compare-direction">Change from <span className="text-[var(--text)]">{older.title}</span> → <span className="text-[var(--text)]">{newer.title}</span></p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="compare-deltas">
                {deltas.map((item) => {
                  const up = item.delta > 0, down = item.delta < 0;
                  const Icon = up ? TrendingUp : down ? TrendingDown : Minus;
                  const color = up ? "var(--green)" : down ? "var(--danger)" : "var(--text-faint)";
                  const fmt = (v) => (item.isMoney ? fmtMoney(v) : Math.round(v));
                  return (
                    <div key={item.key} className="rounded-[12px] border border-[color:var(--border-subtle)] bg-white p-3" data-testid={`compare-delta-${item.key}`}>
                      <div className="text-[11px] text-[var(--text-faint)] truncate">{item.key}</div>
                      <div className="font-serif-display text-xl text-[var(--text)] tabular-nums">{fmt(item.cur)}</div>
                      <div className="inline-flex items-center gap-1 text-[12px] mt-0.5" style={{ color }}><Icon className="h-3.5 w-3.5" />{item.delta > 0 ? "+" : ""}{item.isMoney ? fmtMoney(item.delta) : Math.round(item.delta)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <p className="mt-4 text-sm text-[var(--text-muted)]" data-testid="compare-hint">Pick two different reports above to see what changed between them.</p>
          )}
        </SectionCard>
      )}

      {reports.length === 0 ? (
        <SectionCard testid="reports-empty"><EmptyState icon={FileText} title="No reports yet" message="Save a report from the Command Central overview or the Strategist AI to build your history." /></SectionCard>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reports.map((r) => { const overall = r.snapshot?.overall ?? 0; return (
            <SectionCard key={r.id} testid={`report-card-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-serif-display text-lg text-[var(--text)] truncate">{r.title}</div>
                  <div className="text-[11px] text-[var(--text-muted)] mt-0.5">{r.horizon} · {r.date ? periodLabel(r.horizon, r.date) : ""} · saved {fmtWhen(r.created_at)}</div>
                </div>
                <span className="font-serif-display text-2xl tabular-nums shrink-0" style={{ color: statusStyles[scoreTone(overall)].color }}>{overall}</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {["wealth", "life", "wellness"].map((k) => (<span key={k} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] bg-[color:rgba(23,35,31,0.05)] text-[var(--text-muted)] capitalize">{k} {r.snapshot?.[k]?.score ?? "—"}</span>))}
                {r.ai && <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] bg-[color:rgba(198,139,60,0.14)] text-[var(--gold)]"><Sparkles className="h-3 w-3" />AI</span>}
              </div>
              <div className="mt-4 flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setActive(r)} data-testid={`report-view-${r.id}`} className="border-[color:var(--border-subtle)]"><Eye className="h-3.5 w-3.5 mr-1.5" />View</Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-[var(--danger)] ml-auto" onClick={() => remove(r.id)} data-testid={`report-delete-${r.id}`} aria-label="Delete"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </SectionCard>
          ); })}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="report-detail-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-xl">{active?.title}</DialogTitle></DialogHeader>
          {active && (
            <div className="space-y-4" data-testid="report-detail-body">
              <div className="text-[12px] text-[var(--text-muted)]">{active.horizon} · {active.date ? periodLabel(active.horizon, active.date) : ""} · saved {fmtWhen(active.created_at)}</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-[12px] border border-[color:var(--border-subtle)] bg-white p-3"><div className="text-[11px] text-[var(--text-faint)]">Whole-life</div><div className="font-serif-display text-2xl text-[var(--text)]">{active.snapshot?.overall ?? "—"}</div></div>
                <div className="rounded-[12px] border border-[color:var(--border-subtle)] bg-white p-3"><div className="text-[11px] text-[var(--text-faint)]">Wealth</div><div className="font-serif-display text-2xl text-[var(--text)]">{active.snapshot?.wealth?.score ?? "—"}</div><div className="text-[11px] text-[var(--text-muted)]">{fmtMoney(active.snapshot?.wealth?.netWorth || 0)}</div></div>
                <div className="rounded-[12px] border border-[color:var(--border-subtle)] bg-white p-3"><div className="text-[11px] text-[var(--text-faint)]">Life</div><div className="font-serif-display text-2xl text-[var(--text)]">{active.snapshot?.life?.score ?? "—"}</div></div>
                <div className="rounded-[12px] border border-[color:var(--border-subtle)] bg-white p-3"><div className="text-[11px] text-[var(--text-faint)]">Wellness</div><div className="font-serif-display text-2xl text-[var(--text)]">{active.snapshot?.wellness?.score ?? "—"}</div></div>
              </div>
              {active.ai && (
                <div className="rounded-[12px] border border-[color:rgba(198,139,60,0.35)] bg-[color:rgba(198,139,60,0.06)] p-4">
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[var(--gold)]"><Sparkles className="h-3.5 w-3.5" />AI overview</div>
                  <h4 className="font-serif-display text-lg text-[var(--text)] mt-1.5">{active.ai.headline}</h4>
                  <p className="text-sm text-[var(--text-muted)] mt-1">{active.ai.summary}</p>
                  {(active.ai.priorities || []).length > 0 && (
                    <ul className="mt-3 space-y-1">{active.ai.priorities.map((p, i) => (<li key={i} className="text-[13px] text-[var(--text)]">• {p}</li>))}</ul>
                  )}
                </div>
              )}
              <div className="flex justify-end"><Button onClick={() => window.print()} className="bg-[var(--green)] text-[#f7fbf9]" data-testid="report-print-button"><Printer className="h-4 w-4 mr-1.5" />Print / Save PDF</Button></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
